function asyncGeneratorStep(n, t, e, r, o, a, c) { try { var i = n[a](c), u = i.value; } catch (n) { return void e(n); } i.done ? t(u) : Promise.resolve(u).then(r, o); }
function _asyncToGenerator(n) { return function () { var t = this, e = arguments; return new Promise(function (r, o) { var a = n.apply(t, e); function _next(n) { asyncGeneratorStep(a, r, o, _next, _throw, "next", n); } function _throw(n) { asyncGeneratorStep(a, r, o, _next, _throw, "throw", n); } _next(void 0); }); }; }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
/* eslint class-methods-use-this: "off" */
import memoize from 'memoize-one';
import { GridRange } from '@deephaven/grid';
import Log from '@deephaven/log';
import { Formatter, TableUtils } from '@deephaven/jsapi-utils';
import { assertNotNull, EMPTY_ARRAY, EventShimCustomEvent } from '@deephaven/utils';
import IrisGridTableModelTemplate from "./IrisGridTableModelTemplate.js";
import IrisGridModel from "./IrisGridModel.js";
import IrisGridUtils from "./IrisGridUtils.js";
var log = Log.module('IrisGridTreeTableModel');
var VirtualGroupColumn = Object.freeze({
  name: '__DH_UI_GROUP__',
  displayName: 'Group',
  type: TableUtils.dataType.STRING,
  constituentType: TableUtils.dataType.STRING,
  isPartitionColumn: false,
  isSortable: false,
  isProxy: true,
  description: 'Key column',
  index: -1,
  filter: () => {
    throw new Error('Filter not implemented for virtual column');
  },
  sort: () => {
    throw new Error('Sort not implemented for virtual column');
  },
  formatColor: () => {
    throw new Error('Color not implemented for virtual column');
  },
  get: () => {
    throw new Error('get not implemented for virtual column');
  },
  getFormat: () => {
    throw new Error('getFormat not implemented for virtual column');
  },
  formatNumber: () => {
    throw new Error('formatNumber not implemented for virtual column');
  },
  formatDate: () => {
    throw new Error('formatDate not implemented for virtual column');
  }
});
/**
 * Check if a row is a UITreeRow
 * @param row The row to check
 * @returns True if the row is a UITreeRow and false otherwise
 */
export function isUITreeRow(row) {
  return row != null && typeof row === 'object' && 'hasChildren' in row && 'isExpanded' in row && 'depth' in row && typeof row.hasChildren === 'boolean' && typeof row.isExpanded === 'boolean' && typeof row.depth === 'number';
}
function isLayoutTreeTable(table) {
  return table.layoutHints !== undefined;
}
export function isIrisGridTreeTableModel(tableModel) {
  return tableModel.showExtraGroupColumn != null;
}
class IrisGridTreeTableModel extends IrisGridTableModelTemplate {
  constructor(dh, table) {
    var formatter = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : new Formatter(dh);
    var inputTable = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : null;
    var columnAlignmentMap = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : new Map();
    super(dh, table, formatter, inputTable, columnAlignmentMap);
    /** We keep a virtual column at the front that tracks the "group" that is expanded */
    _defineProperty(this, "virtualColumns", void 0);
    _defineProperty(this, "showExtraGroupCol", true);
    _defineProperty(this, "getCachedColumns", memoize((virtualColumns, tableColumns) => [...virtualColumns, ...tableColumns]));
    _defineProperty(this, "getCachedGroupColumns", memoize((virtualColumns, tableGroupedColumns) => [...virtualColumns, ...tableGroupedColumns]));
    _defineProperty(this, "getCachedFilterableColumnSet", memoize((columns, isAggregatedColumnsAvailable, aggregatedColumns, groupedColumns, virtualColumns) => {
      if (isAggregatedColumnsAvailable) {
        return new Set(
        // Virtual and aggregated columns are not filterable
        columns.filter(c => !virtualColumns.includes(c) && !aggregatedColumns.includes(c)).map(c1 => columns.findIndex(c2 => c1.name === c2.name)));
      }
      // For compatibility with the Enterprise JSAPI,
      // only groupedColumns are filterable on rollup tables
      // if aggregatedColumns API is not available.
      return new Set(((groupedColumns === null || groupedColumns === void 0 ? void 0 : groupedColumns.length) > 0 ? groupedColumns : columns).filter(c => !virtualColumns.includes(c)).map(c1 => columns.findIndex(c2 => c1.name === c2.name)));
    }));
    _defineProperty(this, "getCachedGroupedColumnSet", memoize(groupedColumns => new Set(groupedColumns.map(c1 => this.columns.findIndex(c2 => c1.name === c2.name)))));
    this.virtualColumns = this.showExtraGroupColumn && table.groupedColumns.length > 1 ? [VirtualGroupColumn] : [];
  }
  get showExtraGroupColumn() {
    return this.showExtraGroupCol;
  }
  set showExtraGroupColumn(showExtraGroupCol) {
    if (this.showExtraGroupCol === showExtraGroupCol) {
      return;
    }
    this.showExtraGroupCol = showExtraGroupCol;
    this.updateVirtualColumns();
  }
  updateVirtualColumns() {
    this.virtualColumns = this.showExtraGroupColumn && this.table.groupedColumns.length > 1 ? [VirtualGroupColumn] : [];
    this.dispatchEvent(new EventShimCustomEvent(IrisGridModel.EVENT.COLUMNS_CHANGED, {
      detail: this.columns
    }));
  }
  applyBufferedViewport(viewportTop, viewportBottom, columns) {
    var viewportColumns = [
    // Need to always fetch the grouped columns so we always have key data for the rows
    // Used to display our virtual key column
    ...this.table.groupedColumns, ...columns.filter(c => !this.virtualColumns.includes(c) && !this.groupedColumns.includes(c))];
    this.table.setViewport(viewportTop, viewportBottom, viewportColumns);
  }
  textForCell(x, y) {
    var column = this.sourceColumn(x, y);
    var row = this.row(y);
    if (row != null && column != null) {
      if (!row.hasChildren && column.constituentType != null) {
        var _this$columns$x;
        var value = this.valueForCell(x, y);
        var text = this.displayString(value, column.constituentType, column.name);
        if (TableUtils.isTextType((_this$columns$x = this.columns[x]) === null || _this$columns$x === void 0 ? void 0 : _this$columns$x.type)) {
          if (value === null) {
            return this.formatter.showNullStrings ? 'null' : '';
          }
          if (text === '') {
            return this.formatter.showEmptyStrings ? 'empty' : '';
          }
        }
        return text;
      }

      // Show empty string instead of null in rollup grouping columns (issue #1483)
      if (row.hasChildren && row.depth <= x + 1 && this.valueForCell(x, y) === null && this.getCachedGroupedColumnSet(this.groupedColumns).has(x)) {
        return '';
      }
    }
    return super.textForCell(x, y);
  }
  colorForCell(x, y, theme) {
    var data = this.dataForCell(x, y);
    if (data) {
      var format = data.format,
        value = data.value;
      if (value == null || value === '') {
        assertNotNull(theme.nullStringColor);
        return theme.nullStringColor;
      }
      if ((format === null || format === void 0 ? void 0 : format.color) != null && format.color !== '') {
        return format.color;
      }

      // Fallback to formatting based on the value/type of the cell
      if (value != null) {
        var column = this.sourceColumn(x, y);
        var row = this.row(y);
        assertNotNull(row);
        var columnTypeForFormatting = column.type;

        // For tree table leaf nodes, use the constituent type for formatting
        if (isUITreeRow(row) && row.hasChildren === false && column.constituentType != null) {
          columnTypeForFormatting = column.constituentType;
        }
        return IrisGridUtils.colorForValue(theme, columnTypeForFormatting, column.name, value);
      }
    }
    return theme.textColor;
  }
  textAlignForCell(x, y) {
    var _this$columnAlignment;
    var column = this.sourceColumn(x, y);
    var userTextAlignment = (_this$columnAlignment = this.columnAlignmentMap) === null || _this$columnAlignment === void 0 ? void 0 : _this$columnAlignment.get(column.name);
    if (userTextAlignment != null) {
      return userTextAlignment;
    }
    var row = this.row(y);
    var typeForFormatting = column.type;

    // For tree table leaf nodes, use the constituent type for formatting
    if (isUITreeRow(row) && row.hasChildren === false && column.constituentType != null) {
      typeForFormatting = column.constituentType;
    }
    return IrisGridUtils.textAlignForValue(typeForFormatting, column.name);
  }
  extractViewportRow(row, columns) {
    var isExpanded = row.isExpanded,
      hasChildren = row.hasChildren,
      depth = row.depth;
    var extractedRow = super.extractViewportRow(row, columns);
    var modifiedData = new Map(extractedRow.data);
    if (hasChildren) {
      for (var i = 0; i < this.virtualColumns.length; i += 1) {
        var key = i + (depth - 1) + (this.virtualColumns.length - 1);
        // The root/total row (depth 1) has no grouping key, so `key` points at
        // a virtual column rather than a real grouped source column. Skip it
        // instead of warning.
        if (key >= this.virtualColumns.length) {
          var cellData = modifiedData.get(key);
          if (cellData == null) {
            log.warn('Missing key data for virtual column', i, depth, key, row);
          } else {
            modifiedData.set(i, cellData);
          }
        }
      }
    }
    return _objectSpread(_objectSpread({}, extractedRow), {}, {
      data: modifiedData,
      isExpanded,
      hasChildren,
      depth
    });
  }
  snapshot(ranges, includeHeaders) {
    var _arguments = arguments,
      _this = this;
    return _asyncToGenerator(function* () {
      var _this$viewport$column;
      var formatValue = _arguments.length > 2 && _arguments[2] !== undefined ? _arguments[2] : value => value;
      assertNotNull(_this.viewport);
      assertNotNull(_this.viewportData);
      var columns = (_this$viewport$column = _this.viewport.columns) !== null && _this$viewport$column !== void 0 ? _this$viewport$column : _this.columns;
      var result = [];
      if (includeHeaders != null && includeHeaders) {
        result.push(columns.map(c => c.name));
      }
      var viewportRange = new GridRange(0, _this.viewportData.offset, columns.length, _this.viewportData.offset + _this.viewportData.rows.length - 1);
      for (var i = 0; i < ranges.length; i += 1) {
        var intersection = GridRange.intersection(viewportRange, ranges[i]);
        assertNotNull(intersection);
        assertNotNull(intersection.startRow);
        assertNotNull(intersection.endRow);
        for (var r = intersection.startRow; r <= intersection.endRow; r += 1) {
          var resultRow = [];
          var viewportRow = _this.viewportData.rows[r - _this.viewportData.offset];
          assertNotNull(intersection.startColumn);
          assertNotNull(intersection.endColumn);
          for (var c = intersection.startColumn; c <= intersection.endColumn; c += 1) {
            var _viewportRow$data$get;
            resultRow.push(formatValue((_viewportRow$data$get = viewportRow.data.get(c)) === null || _viewportRow$data$get === void 0 ? void 0 : _viewportRow$data$get.value, _this.columns[c]));
          }
          result.push(resultRow);
        }
      }
      return result;
    })();
  }
  get columns() {
    return this.getCachedColumns(this.virtualColumns, super.columns);
  }
  get aggregatedColumns() {
    return this.isAggregatedColumnsAvailable ? this.table.aggregatedColumns : EMPTY_ARRAY;
  }
  get groupedColumns() {
    return this.getCachedGroupColumns(this.virtualColumns, this.table.groupedColumns);
  }
  sourceForCell(column, row) {
    if (column >= this.virtualColumns.length) {
      return {
        column,
        row
      };
    }
    return {
      column: this.virtualSourceColumnIndex(column, row),
      row
    };
  }
  sourceColumn(column, row) {
    if (column >= this.virtualColumns.length) {
      return super.sourceColumn(column, row);
    }
    return this.columns[this.virtualSourceColumnIndex(column, row)];
  }

  /**
   * Get the index of the grouped column that the virtual group column proxies
   * for the given cell.
   */
  virtualSourceColumnIndex(column, row) {
    var depth = this.depthForRow(row);
    return Math.min(column + depth, this.groupedColumns.length - 1);
  }
  getClearFilterRange(column) {
    if (column >= this.virtualColumns.length) {
      return super.getClearFilterRange(column);
    }
    // Source for the proxied column could be any of the grouped columns.
    // Return the range of columns matching the grouped columns.
    return [this.virtualColumns.length, this.groupedColumns.length];
  }
  get layoutHints() {
    if (isLayoutTreeTable(this.table)) {
      return this.table.layoutHints;
    }
    return undefined;
  }
  get hasExpandableRows() {
    return true;
  }
  get isAggregatedColumnsAvailable() {
    return (
      // aggregatedColumns are not available in the Legacy API
      // and Core API before v0.39.4
      this.table.aggregatedColumns !== undefined
    );
  }
  get isExpandAllAvailable() {
    return this.table.expandAll !== undefined;
  }
  get isChartBuilderAvailable() {
    return false;
  }
  get isOrganizeColumnsAvailable() {
    return true;
  }
  get isSelectDistinctAvailable() {
    return false;
  }
  get isReversible() {
    return false;
  }
  isFilterable(columnIndex) {
    return this.getCachedFilterableColumnSet(this.columns, this.isAggregatedColumnsAvailable, this.aggregatedColumns, this.groupedColumns, this.virtualColumns).has(columnIndex);
  }
  isColumnMovable(column) {
    return column >= this.groupedColumns.length;
  }
  isRowExpandable(y) {
    var _row$hasChildren;
    var row = this.row(y);
    return (_row$hasChildren = row === null || row === void 0 ? void 0 : row.hasChildren) !== null && _row$hasChildren !== void 0 ? _row$hasChildren : false;
  }
  isRowExpanded(y) {
    var _row$isExpanded;
    var row = this.row(y);
    return (_row$isExpanded = row === null || row === void 0 ? void 0 : row.isExpanded) !== null && _row$isExpanded !== void 0 ? _row$isExpanded : false;
  }
  setRowExpanded(y, isExpanded) {
    var expandDescendants = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
    if (this.isExpandAllAvailable) {
      this.table.setExpanded(y, isExpanded, expandDescendants);
    } else {
      this.table.setExpanded(y, isExpanded);
    }
  }
  expandAll() {
    if (this.table.expandAll != null) {
      this.table.expandAll();
    }
  }
  collapseAll() {
    if (this.table.collapseAll != null) {
      this.table.collapseAll();
    }
  }
  depthForRow(y) {
    var _row$depth;
    var row = this.row(y);
    return ((_row$depth = row === null || row === void 0 ? void 0 : row.depth) !== null && _row$depth !== void 0 ? _row$depth : 1) - 1;
  }
  updateFrozenColumns(columns) {
    if (columns.length > 0) {
      throw new Error('Cannot freeze columns on a tree table');
    }
  }
}
export default IrisGridTreeTableModel;
//# sourceMappingURL=IrisGridTreeTableModel.js.map